"use client";

import { useEffect, useRef, useState } from "react";
import { Modal } from "@/components/ui/modal";
import {
  PUBLIC_RELEASE_NOTES,
  PUBLIC_RELEASE_SECTION_NAMES,
  formatPublicReleaseDate,
} from "@/content/public-release-notes";
import { PRODUCT_RELEASE, PRODUCT_RELEASE_LABEL } from "@/lib/product-release";

export function PublicReleaseNotesTrigger({
  mobile = false,
}: {
  mobile?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (open) {
      wasOpenRef.current = true;
      return;
    }

    if (wasOpenRef.current) {
      triggerRef.current?.focus();
      wasOpenRef.current = false;
    }
  }, [open]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={
          mobile
            ? "rounded-md px-1 py-0.5 text-xs text-slate-500 underline decoration-slate-300 underline-offset-4 transition hover:text-[#006DFF] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#006DFF] focus-visible:ring-offset-2"
            : "rounded-md px-1 py-0.5 font-semibold tracking-[0.16em] text-[#0B1E3B] transition hover:text-[#006DFF] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#006DFF] focus-visible:ring-offset-2"
        }
        onClick={() => setOpen(true)}
        aria-label={`Ver novidades da versão ${PRODUCT_RELEASE_LABEL}`}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        {mobile ? "Nexus" : "NEXUS"} v{PRODUCT_RELEASE.version}
        <span aria-hidden="true"> · </span>
        {PRODUCT_RELEASE.name}
      </button>

      <Modal
        title="Novidades do Nexus"
        description="Confira as principais mudanças das versões mais recentes."
        open={open}
        onClose={() => setOpen(false)}
        size="lg"
        height="fixed"
      >
        <div className="space-y-3">
          {PUBLIC_RELEASE_NOTES.map((note) => (
            <details
              key={note.version}
              open={note.status === "released" || note.status === "release-candidate" ? true : undefined}
              className="group rounded-xl border border-slate-200 bg-slate-50/70 dark:border-slate-800 dark:bg-slate-900/50"
            >
              <summary className="flex cursor-pointer list-none items-start justify-between gap-4 px-4 py-3 [&::-webkit-details-marker]:hidden">
                <span className="min-w-0">
                  <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm font-semibold text-slate-950 dark:text-slate-50">
                    <span>{note.version} {note.codename}</span>
                    <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-medium text-blue-700 dark:bg-blue-950/60 dark:text-blue-300">
                      {note.status === "released"
                        ? "Lançamento oficial"
                        : note.status === "release-candidate"
                        ? "Candidato a lançamento"
                        : "Marco interno"}
                    </span>
                  </span>
                  <span className="mt-1 block text-xs text-slate-500 dark:text-slate-400">
                    {formatPublicReleaseDate(note.date)} · {note.title}
                  </span>
                </span>
                <span className="shrink-0 text-slate-400 transition-transform group-open:rotate-180" aria-hidden="true">
                  ▾
                </span>
              </summary>
              <div className="border-t border-slate-200 px-4 pb-4 pt-3 dark:border-slate-800">
                <p className="text-sm leading-6 text-slate-600 dark:text-slate-300">
                  {note.summary}
                </p>
                {note.changes?.length ? (
                  <div className="mt-4 space-y-3">
                    {note.changes.map((change) => (
                      <article key={`${change.type}-${change.module}-${change.title}`}>
                        <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                          {change.type} – {change.module}: {change.title}
                        </h3>
                        <p className="mt-1 text-sm leading-5 text-slate-600 dark:text-slate-300">
                          {change.description}
                        </p>
                      </article>
                    ))}
                  </div>
                ) : null}
                {!note.changes?.length ? <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  {PUBLIC_RELEASE_SECTION_NAMES.map((sectionName) => {
                    const items = note.sections[sectionName];
                    if (!items?.length) return null;

                    return (
                      <section key={sectionName}>
                        <h3 className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400">
                          {sectionName}
                        </h3>
                        <ul className="mt-2 space-y-2 text-sm leading-5 text-slate-700 dark:text-slate-300">
                          {items.map((item) => (
                            <li key={item} className="flex gap-2">
                              <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#006DFF]" aria-hidden="true" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </section>
                    );
                  })}
                </div> : null}
              </div>
            </details>
          ))}
        </div>
      </Modal>
    </>
  );
}
